import { Injectable } from '@angular/core';
import { Either, Left, Right } from 'purify-ts';
import { BulkRepositoryFailure } from '../../domain/bulk/failure';
import { IBulkRepository } from '../../domain/bulk/i-bulk-repository';
import {
  BulkJob,
  BulkJobAccepted,
  BulkJobFailure,
  BulkJobOptions,
  SendBulkProps,
} from '../../domain/bulk/types';

const DEFAULT_FAILURES_LIMIT = 50;

interface FakeJob {
  job: Omit<BulkJob, 'failures' | 'failuresTotal' | 'failuresNextOffset'>;
  failures: BulkJobFailure[];
}

@Injectable({
  providedIn: 'root',
})
export class FakeBulkRepository implements IBulkRepository {
  private jobs = new Map<string, FakeJob>();

  public async sendTemplateBulk(
    props: SendBulkProps
  ): Promise<Either<BulkRepositoryFailure, BulkJobAccepted>> {
    const bulkJobId = `bulk-${Date.now()}`;
    const total = props.recipients.length;

    this.jobs.set(bulkJobId, {
      job: {
        bulkJobId,
        total,
        sent: 0,
        failed: 0,
        pending: total,
        status: 'PROCESSING',
      },
      failures: [],
    });

    this.advance(bulkJobId);

    return Right({ bulkJobId, total });
  }

  public async getJob(
    bulkJobId: string,
    options: BulkJobOptions = {}
  ): Promise<Either<BulkRepositoryFailure, BulkJob>> {
    const current = this.jobs.get(bulkJobId);

    if (!current) {
      return Left(BulkRepositoryFailure.failToComunicateWithTheServer());
    }

    const offset = options.failuresOffset ?? 0;
    const limit = options.failuresLimit ?? DEFAULT_FAILURES_LIMIT;
    const page = current.failures.slice(offset, offset + limit);
    const nextOffset = offset + page.length;

    return Right({
      ...current.job,
      failures: page,
      failuresTotal: current.failures.length,
      failuresNextOffset: nextOffset < current.failures.length ? nextOffset : null,
    });
  }

  private advance(bulkJobId: string): void {
    const tick = setInterval(() => {
      const current = this.jobs.get(bulkJobId);

      if (!current || current.job.pending === 0) {
        clearInterval(tick);
        return;
      }

      current.job.sent += 1;
      current.job.pending -= 1;
      current.job.status = current.job.pending === 0 ? 'DONE' : 'PROCESSING';
    }, 400);
  }
}
